import React, { useState, useEffect, useMemo, memo } from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { isEmpty, uniq, filter } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
} from "react-virtualized";
import ColumnHeader from "../ColumnHeader";
import { useLazyGetSSTotalQuery } from "../../../services/ssTotalApi";
import usePermissions from "../../../hooks/usePermissions";
import useStableCallback from "../../../hooks/useStableCallback";
import CommonDialog from "../../../components/CommonDialog";
import PermissionsGate from "../../../permissions/PermissionsGate";
import { SCOPES } from "../../../permissions/permissions-map";
import CustomDrawer from "../../../components/CustomDrawer";
import FileExport from "../../../components/FileExport";
import CustomFloatingButton from "../../../components/CustomFloatingButton";
import useRequest from "../../../hooks/useRequest";
import StoreSuppliesTotals from "../StoreSuppliesTotals";
import SettingsForm from "../SettingsForm";
import SSPogFilter, {
  SKU_AUTOCOMPLETE_INDEX,
  VENDOR_AUTOCOMPLETE_INDEX,
} from "../SSPogFilter";
import StoreSuppliesListContainer from "../StoreSuppliesListContainer";
import CustomNoResult from "../../ManagePlanograms/CustomNoResult";
import { validPog } from "../../../utils/pogValidator";
import { ENTITY_TYPES, KeepCacheAsIs, singleValue } from "../../../utils";
import {
  showNotifier,
  setGlobalLoader,
  resetItemFilters,
  resetPogFilters,
} from "../../../store/globalSlice";
import filterHandler from "../../../utils/filterHandler";
import {
  saveUpdatedPogs,
  prepareInitialData,
  checkPogsBeforeSave,
  prepareSettingsSaveData,
  postSettingsSaveUpdateData,
  prepareInlineSaveData,
  postInlineSaveUpdateData,
  saveUpdatedInlinePogs,
  updateDuplicatePogNames,
  resetPogFieldsOnSelection,
  prepareCreatePOData,
  postCreatePOUpdateData,
  prepareSaveDatesData,
  postSaveDatesUpdateData,
  prepareDeleteDatesData,
  postDeleteDatesUpdateData,
} from "./prepareData";
import configData from "../../../config.js";
import SSPog from "../SSPog";
import { isItemDepartment } from "../../../utils/deptValidator";

const OVERSCAN_ROW_COUNT = 10;
const pogCache = new CellMeasurerCache({
  fixedWidth: true,
  fixedHeight: true,
  defaultHeight: 70,
});

function StoreSuppliesPogs() {
  const dispatch = useDispatch();
  const { requestMaker } = useRequest();
  const { hasPermission } = usePermissions();
  const [selectedOriginalPogNames, setSelectedOriginalPogNames] = useState([]);
  const [selectedPogNames, setSelectedPogNames] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [pogsLoading, setPogsLoading] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  // selections are those which items user selected using checkbox, it contains item id only.
  const [selections, setSelections] = useState([]);
  // valid selections are those which shows valid items to be eligible for selection, it contains entire item object.
  const [validSelections, setValidSelections] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const user = useSelector((store) => store.global.user);
  const store = useSelector((store) => store.global.store);
  const pogFilters = useSelector((store) => store.global.pogFilters);

  const [getSSTotal] = useLazyGetSSTotalQuery();
  // No need of client side filter if async filter is selected.
  const skipFilter =
    !!pogFilters?._departmentLetter ||
    !!pogFilters?._planogramNumber ||
    !!pogFilters?._vendor ||
    !!pogFilters?._sku;

  useEffect(() => {
    dispatch(resetItemFilters());
    getDuplicatePogs();
    getPogList(
      {
        storeId: store.id,
        departmentLetter: pogFilters?._departmentLetter || "",
        category: null,
        planogramValue: pogFilters?._planogramNumber || null,
        vendorName: pogFilters?._vendor || null,
        skuNumber: pogFilters?._sku || null,
      },
      true,
      skipFilter
    );
    // eslint-disable-next-line
  }, []);

  const isRowLoaded = ({ index }) =>
    index < filteredData.length && !filteredData[index]?.virtualLoading;

  const isXPogSelected = useMemo(() => {
    let xPogSelected = false;
    if (selections?.length && filteredData.length) {
      for (let i = 0, len = filteredData.length; i < len; i++) {
        const _pog = filteredData[i];
        if (
          selections?.includes(_pog?.PlanogramNumber) &&
          isItemDepartment(_pog)
        ) {
          xPogSelected = true;
          break;
        }
      }
    }
    return xPogSelected;
  }, [selections, filteredData]);

  const loadMoreRows = ({ startIndex, stopIndex }) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const d = [...filteredData];
        for (let idx = startIndex; idx <= stopIndex; idx++) {
          d[idx].virtualLoading = false;
        }
        setFilteredData(d);
        resolve();
      }, 800);
    });
  };

  const getDuplicatePogs = useStableCallback(() => {
    requestMaker({
      method: "post",
      url: configData.SSDuplicatePogs,
      data: {
        storeId: store.id,
      },
    }).then((data) => {
      const list = data?.data || [];
      setSelectedOriginalPogNames(list);
      setSelectedPogNames(list);
    });
  });

  const getPogList = useStableCallback(
    (
      filterValues,
      skipGlobalLoader = false,
      skipFilter = false,
      isAutoSelect = false
    ) => {
      !skipGlobalLoader && dispatch(setGlobalLoader(true));
      requestMaker({
        method: "post",
        url: configData.GetSSPogList,
        data: filterValues,
      })
        .then((data) => {
          const fetchedData = data?.data || [];
          const pogList = prepareInitialData(fetchedData);
          const updatedItems = skipFilter
            ? pogList
            : filterHandler(
                pogFilters,
                pogList,
                pogList,
                ENTITY_TYPES.ssPlanogram
              );
          setValidSelections(updatedItems.filter((pog) => validPog(pog)));
          setFilteredData(() => {
            if (isAutoSelect) {
              return updatedItems.map((p) => ({
                ...p,
                virtualLoading: false,
              }));
            }
            return updatedItems;
          });
          setData(pogList);
          setOriginalData(pogList);
          if (isAutoSelect) {
            setSelectAll(false);
            setSelections([]);
            dispatch(
              showNotifier({
                message: "Auto planogram selection successfully completed.",
              })
            );
            getSSTotal({ storeId: store?.id, type: "" });
            getDuplicatePogs();
          }
        })
        .catch(() => {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        })
        .finally(() => {
          setPogsLoading(false);
          !skipGlobalLoader && dispatch(setGlobalLoader(false));
        });
    }
  );

  const toggleDrawer = useStableCallback((open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setIsDrawerOpen(open);
  });

  const handlePogListReset = useStableCallback(() => {
    setShowResetDialog(true);
  });

  const handleResetDialogClose = useStableCallback(() => {
    setShowResetDialog(false);
  });

  const handleResetDialogDone = useStableCallback(() => {
    const updatedData = filterHandler(
      pogFilters,
      originalData,
      originalData,
      ENTITY_TYPES.ssPlanogram
    );
    const validPogs = updatedData.filter((pog) => validPog(pog));
    setValidSelections(validPogs);
    setFilteredData(() => KeepCacheAsIs(filteredData, updatedData));
    setData(() => originalData);
    setSelectAll(false);
    setSelections([]);
    setSelectedPogNames(selectedOriginalPogNames);
    handleResetDialogClose();
    window.scrollTo(0, 0);
  });

  const handleFilterSubmit = useStableCallback(() => {
    // If after async filter, if user tries to apply sync(Client side) filter then refetch entire list.
    if (
      pogFilters._prevViewBy &&
      [SKU_AUTOCOMPLETE_INDEX, VENDOR_AUTOCOMPLETE_INDEX].includes(
        pogFilters._prevViewBy
      )
    ) {
      setSelectAll(false);
      setSelections([]);
      setData([]);
      setFilteredData([]);
      setOriginalData([]);
      setPogsLoading(true);
      setSelectedOriginalPogNames([]);
      setSelectedPogNames([]);
      getDuplicatePogs();
      getPogList({
        storeId: store.id,
        departmentLetter: "",
        category: null,
        planogramValue: null,
        vendorName: null,
        skuNumber: null,
      });
      dispatch(resetPogFilters({ set: { key: "_prevViewBy", value: "" } }));
      return false;
    }
    const updatedData = filterHandler(
      pogFilters,
      data,
      originalData,
      ENTITY_TYPES.ssPlanogram
    );
    const validPogs = updatedData.filter((pog) => validPog(pog));
    pogCache.clearAll();
    setValidSelections(validPogs);
    setFilteredData(updatedData);
    const selectionCount = updatedData.filter((i) => !!i?.selection);
    if (
      selectionCount.length === selections.length &&
      selections.length === updatedData.length &&
      selections.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  });

  const handleFilterReset = useStableCallback((filterOption) => {
    if (
      [SKU_AUTOCOMPLETE_INDEX, VENDOR_AUTOCOMPLETE_INDEX].includes(filterOption)
    ) {
      setFilteredData([]);
      setData([]);
      setOriginalData([]);
      setPogsLoading(true);
      setSelectedOriginalPogNames([]);
      setSelectedPogNames([]);
      getDuplicatePogs();
      getPogList({
        storeId: store.id,
        departmentLetter: "",
        category: null,
        planogramValue: null,
        vendorName: null,
        skuNumber: null,
      });
      pogCache.clearAll();
      return true;
    }
    const updatedData = filterHandler(
      pogFilters,
      data,
      originalData,
      ENTITY_TYPES.ssPlanogram
    );
    const validPogs = updatedData.filter((pog) => validPog(pog));
    pogCache.clearAll();
    setValidSelections(validPogs);
    setFilteredData(updatedData);
  });

  const handleFilterSort = useStableCallback(() => {
    const updatedData = filterHandler(
      pogFilters,
      filteredData,
      originalData,
      ENTITY_TYPES.ssPlanogram
    );
    const validItems = updatedData.filter((pog) => validPog(pog));
    setValidSelections(validItems);
    setFilteredData(KeepCacheAsIs(filteredData, updatedData));
  });

  const handleSKUFilter = useStableCallback(() => {
    setFilteredData([]);
    setData([]);
    setOriginalData([]);
    setPogsLoading(true);
    const filter = pogFilters?.filterValue || "";
    const filterDetails = filter?.split("-");
    const filterValues = {
      storeId: store.id,
      departmentLetter: filterDetails[0] || null,
      category: null,
      planogramValue: filterDetails[1] || null,
      vendorName: null,
      skuNumber: filterDetails[2] || null,
    };
    getPogList(filterValues, false, true);
  });

  const handleVendorFilter = useStableCallback(() => {
    setFilteredData([]);
    setData([]);
    setOriginalData([]);
    setPogsLoading(true);
    const filter = pogFilters?.filterValue || "";
    const filterValues = {
      storeId: store.id,
      departmentLetter: null,
      category: null,
      planogramValue: null,
      vendorName: filter,
      skuNumber: null,
    };
    getPogList(filterValues, false, true);
  });

  const handleAutoSelectFromOSO = useStableCallback(() => {
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.SSOSOAutoSelect,
      data: {
        storeId: store.id,
        userId: user.email,
      },
    }).then(() => {
      dispatch(setGlobalLoader(false));
      getPogList(
        {
          storeId: store.id,
          departmentLetter: pogFilters?._departmentLetter || "",
          category: null,
          planogramValue: pogFilters?._planogramNumber || null,
          vendorName: pogFilters?._vendor || null,
          skuNumber: pogFilters?._sku || null,
        },
        false,
        false,
        true
      );
    });
  });

  const handleProcessData = useStableCallback(
    (id, PogName) =>
      ({
        field,
        value,
        isPogChecked = false,
        isPogSelected = singleValue.no,
      }) => {
        // Check based on Selection N/Y OR Checked and update duplicate POG name.
        updateDuplicatePogNames({
          field,
          value,
          id,
          PogName,
          setSelectedPogNames,
          isPogSelected,
          isPogChecked,
          originalData,
        });

        const getUpdatedData = (oldPogs) => {
          const updatedData = [...oldPogs];
          let index = -1;
          for (let i = 0, len = oldPogs.length; i < len; i++) {
            const p = oldPogs[i] || {};
            if (p?.PlanogramNumber === id) {
              index = i;
              break;
            }
          }
          let pog = oldPogs[index];
          pog = {
            ...resetPogFieldsOnSelection(field, {
              ...pog,
              [field]: value,
            }),
          };
          updatedData[index] = { ...pog };
          return updatedData;
        };

        setFilteredData(getUpdatedData);
        setData(getUpdatedData);

        // Handles only POG to Select All auto selection functionality.
        if (field === "selection") {
          if (value) {
            setSelections((oldVal) => {
              const updatedSelections = uniq([...oldVal, id]);
              setSelectAll(updatedSelections.length === validSelections.length);
              return updatedSelections;
            });
          } else {
            setSelections((oldVal) => {
              return filter(oldVal, (itemId) => itemId !== id);
            });
            setSelectAll(false);
          }
        }
      }
  );

  const handleInlineSave = useStableCallback((pog) => {
    const updatedData = prepareInlineSaveData(
      pogFilters,
      pog,
      store.id,
      user?.email
    );

    dispatch(setGlobalLoader(true));

    requestMaker({
      method: "post",
      url: configData.SSPogSave,
      data: updatedData,
    })
      .then((res) => {
        if (res?.data.length) {
          const postSaveUpdatedItems = postInlineSaveUpdateData(
            pog,
            filteredData,
            res?.data
          );
          setData(saveUpdatedInlinePogs(postSaveUpdatedItems));
          setFilteredData((prevState) => {
            const getUpdates = saveUpdatedInlinePogs(postSaveUpdatedItems);
            const _partialUpdates = getUpdates(prevState);
            const updatedData = !skipFilter
              ? filterHandler(
                  pogFilters,
                  _partialUpdates,
                  originalData,
                  ENTITY_TYPES.ssPlanogram
                )
              : _partialUpdates;
            return updatedData;
          });
          setOriginalData(saveUpdatedInlinePogs(postSaveUpdatedItems));
          setSelectAll(false);
          setSelections([]);
          dispatch(showNotifier({ message: "POG details saved successfully" }));
          getSSTotal({ storeId: store?.id, type: "" });
          getDuplicatePogs();
        }
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const handleSettingsSave = useStableCallback((selected) => {
    const isValidSaveData = checkPogsBeforeSave(
      dispatch,
      showNotifier,
      selected,
      filteredData,
      selections,
      originalData
    );
    if (!isValidSaveData) {
      return false;
    }
    const updatedData = prepareSettingsSaveData(
      pogFilters,
      { selected },
      store.id,
      user?.email,
      filteredData,
      selections
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.SSPogSave,
      data: updatedData,
    })
      .then((res) => {
        if (res?.data.length) {
          const postSaveUpdatedItems = postSettingsSaveUpdateData(
            { selected },
            filteredData,
            res?.data || [],
            originalData,
            selections
          );
          setData(saveUpdatedPogs(postSaveUpdatedItems));
          setFilteredData((prevState) => {
            const getUpdates = saveUpdatedPogs(postSaveUpdatedItems);
            const _partialUpdates = getUpdates(prevState);
            const updatedData = !skipFilter
              ? filterHandler(
                  pogFilters,
                  _partialUpdates,
                  originalData,
                  ENTITY_TYPES.ssPlanogram
                )
              : _partialUpdates;
            return updatedData;
          });
          setOriginalData(saveUpdatedPogs(postSaveUpdatedItems));
          setSelectAll(false);
          setSelections([]);
          dispatch(showNotifier({ message: "POG details saved successfully" }));
          getSSTotal({ storeId: store?.id, type: "" });
          getDuplicatePogs();
        }
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const onCreatePONumbers = useStableCallback((details) => {
    const isValidSaveData = checkPogsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      originalData,
      null,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    const updatedData = prepareCreatePOData(
      pogFilters,
      filteredData,
      store.id,
      details,
      user?.email,
      selections
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.SSPogCreatePO,
      data: updatedData,
    })
      .then((data) => {
        const postCreatePOUpdatedPogs = postCreatePOUpdateData(
          filteredData,
          data?.data,
          selections
        );
        setFilteredData(saveUpdatedPogs(postCreatePOUpdatedPogs, true));
        setData(saveUpdatedPogs(postCreatePOUpdatedPogs, true));
        setOriginalData(saveUpdatedPogs(postCreatePOUpdatedPogs, true));
        dispatch(
          showNotifier({
            message: "Saved changes to PO number type",
          })
        );
      })
      .catch((error) => {
        if (
          error?.response?.status === 500 &&
          error?.response?.data?.detail?.includes("UNIQUE KEY")
        ) {
          dispatch(
            showNotifier({
              message:
                "Can not generate PO number for selected POGs, since there are some duplicate items present in it.",
              severity: "error",
            })
          );
        } else if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not generate PO number for selected POGs.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
      });
  });

  const checkPOBeforeLocalUpdate = (details = []) => {
    const invalidPogs = [];
    const updatedPogs = details?.data ?? [];
    for (let i = 0; i < updatedPogs.length; i++) {
      const _pog = updatedPogs[i];
      if (_pog?.CountOfPOItems !== 0) {
        invalidPogs.push(_pog?.PogNumber);
      }
    }
    return invalidPogs;
  };

  const checkPOGenerated = useStableCallback(() => {
    let isAllPOGenerated = true;
    const poNumbers = filteredData
      ?.map((i) => {
        if (
          selections.includes(i?.PlanogramNumber) &&
          i?.__original?.Selected === singleValue.yes
        ) {
          return i.PlanogramNumber;
        }
        return undefined;
      })
      ?.filter((i) => !!i);
    if (poNumbers.length === selections.length) {
      isAllPOGenerated = true;
    } else {
      isAllPOGenerated = false;
    }
    return isAllPOGenerated;
  });

  const onSaveDates = useStableCallback((releaseDate) => {
    const isValidSaveData = checkPogsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      originalData,
      null,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    if (!checkPOGenerated()) {
      return dispatch(
        showNotifier({
          message:
            "PO Number for some selected POGs are missing. Please generate PO Number.",
          severity: "error",
        })
      );
    }
    const updatedData = prepareSaveDatesData(
      pogFilters,
      filteredData,
      releaseDate,
      store.id,
      user,
      selections
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.SSPogSaveDate,
      data: updatedData,
    })
      .then((res) => {
        const invalidPogs = checkPOBeforeLocalUpdate(res);
        const postSaveUpdatedItems = postSaveDatesUpdateData(
          releaseDate,
          filteredData,
          selections,
          res?.data ?? []
        );
        setFilteredData(saveUpdatedPogs(postSaveUpdatedItems));
        setData(saveUpdatedPogs(postSaveUpdatedItems));
        setOriginalData(saveUpdatedPogs(postSaveUpdatedItems));
        if (invalidPogs.length) {
          const _updatedPog =
            selections.length === 1
              ? filteredData?.find((i) =>
                  selections.includes(i.PlanogramNumber)
                )
              : {};
          if (
            selections.length === invalidPogs.length &&
            selections.length === 1 &&
            _updatedPog?.SKUs === Math.abs(res?.data[0]?.CountOfPOItems)
          ) {
            dispatch(
              showNotifier({
                message:
                  "Can not save the date for selected POGs. Please generate PO Number.",
                severity: "error",
              })
            );
          } else {
            dispatch(
              showNotifier({
                message:
                  "Release Dates saved successfully for valid POG items having PO number. Please generate PO Number for remaining items.",
                severity: "warning",
              })
            );
          }
        } else {
          dispatch(
            showNotifier({
              message: "Release Dates saved successfully.",
            })
          );
        }
        setSelectAll(false);
        setSelections([]);
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not save the date for selected POGs.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const onDeleteDates = useStableCallback(() => {
    const isValidSaveData = checkPogsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      originalData,
      null,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    const updatedData = prepareDeleteDatesData(
      pogFilters,
      filteredData,
      store.id,
      user?.email,
      selections
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.SSPogDeleteDate,
      data: updatedData,
    })
      .then(() => {
        const postDeleteUpdatedItems = postDeleteDatesUpdateData(
          filteredData,
          selections
        );
        setFilteredData(saveUpdatedPogs(postDeleteUpdatedItems));
        setData(saveUpdatedPogs(postDeleteUpdatedItems));
        setOriginalData(saveUpdatedPogs(postDeleteUpdatedItems));
        setSelectAll(false);
        setSelections([]);
        dispatch(
          showNotifier({
            message: "Release Dates deleted successfully.",
          })
        );
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not delete the date for selected POGs.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const listRenderer = ({ index, key, style, parent }) => {
    const pog = filteredData[index] || {};
    let originalPog = { ...filteredData[index]?.__original };
    const memoizedDataHandler = handleProcessData(
      pog?.PlanogramNumber,
      pog?.PlanogramName
    );
    const isDuplicateSelection =
      !pog.selection &&
      ![singleValue.yes].includes(pog?.Selected) &&
      ![singleValue.yes].includes(originalPog?.Selected) &&
      selectedPogNames.includes(pog?.PlanogramName);

    const freezePog =
      !hasPermission({ scopes: [SCOPES.canEdit] }) ||
      store?.pending ||
      store?.isArchived ||
      store?.dataguideInProgress ||
      store?.isCompleted;

    return (
      <CellMeasurer
        key={key}
        cache={pogCache}
        parent={parent}
        columnIndex={0}
        rowIndex={index}
      >
        {({ registerChild }) => (
          <div style={style} key={key} ref={registerChild}>
            <SSPog
              pog={pog}
              handleProcessData={memoizedDataHandler}
              handleInlineSave={handleInlineSave}
              freezePog={freezePog}
              isDuplicateSelection={isDuplicateSelection}
            />
          </div>
        )}
      </CellMeasurer>
    );
  };

  const hideSettings = useMemo(() => {
    const isSelectedItemFound = filteredData?.find((i) =>
      selections.includes(i?.PlanogramNumber)
    );
    return isEmpty(selections) || !isSelectedItemFound;
  }, [selections, filteredData]);

  return (
    <Container id="page-container" maxWidth={false} sx={{ paddingY: 1 }}>
      <Stack spacing={1}>
        <StoreSuppliesTotals />
        <SSPogFilter
          handleFilter={handleFilterSubmit}
          handleReset={handleFilterReset}
          handleSortChange={handleFilterSort}
          handleSKUFilter={handleSKUFilter}
          handleVendorFilter={handleVendorFilter}
        />
        <StoreSuppliesListContainer
          hideSelectAll
          isResultPresent={!!filteredData.length}
          resultLoading={pogsLoading}
          entityType={ENTITY_TYPES.ssPlanogram}
          stickyHeader={<ColumnHeader entity={ENTITY_TYPES.planogram} />}
          exportButton={<FileExport data={filteredData} type="ssPlanogram" />}
          resetAction={
            <PermissionsGate scopes={[SCOPES.canEdit]}>
              {!pogsLoading && !!filteredData.length && (
                <>
                  <Button
                    variant="outlined"
                    sx={{ alignSelf: "flex-end", mr: 1 }}
                    onClick={handlePogListReset}
                    size="small"
                    disabled={
                      store?.pending ||
                      store?.isArchived ||
                      store?.isCompleted ||
                      store?.dataguideInProgress
                    }
                  >
                    Reset
                  </Button>
                  {!store.isStoreSupplies && (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={
                        store?.pending ||
                        store?.isArchived ||
                        store?.isCompleted ||
                        store?.dataguideInProgress
                      }
                      onClick={handleAutoSelectFromOSO}
                    >
                      Auto Select From OSO
                    </Button>
                  )}
                  <CommonDialog
                    open={showResetDialog}
                    title="Reset All Unsaved POGs"
                    content={
                      "Are you sure, you want to reset/revert all unsaved POG changes?"
                    }
                    type="yesNo"
                    handleClose={handleResetDialogClose}
                    handleDone={handleResetDialogDone}
                  />
                </>
              )}
            </PermissionsGate>
          }
        >
          {filteredData.length ? (
            <div className="_virtual-container">
              <div className="_virtual-list-wrapper">
                <InfiniteLoader
                  isRowLoaded={isRowLoaded}
                  loadMoreRows={loadMoreRows}
                  rowCount={filteredData.length}
                >
                  {({ onRowsRendered, registerChild }) => (
                    <AutoSizer>
                      {({ width, height }) => (
                        <List
                          width={width}
                          height={height}
                          ref={registerChild}
                          deferredMeasurementCache={pogCache}
                          rowHeight={pogCache.rowHeight}
                          rowRenderer={listRenderer}
                          onRowsRendered={onRowsRendered}
                          rowCount={filteredData.length}
                          overscanRowCount={OVERSCAN_ROW_COUNT}
                        />
                      )}
                    </AutoSizer>
                  )}
                </InfiniteLoader>
              </div>
            </div>
          ) : (
            <CustomNoResult
              entity={ENTITY_TYPES.planogram}
              loading={pogsLoading}
            />
          )}
        </StoreSuppliesListContainer>
      </Stack>
      {!hideSettings && <CustomFloatingButton onClick={toggleDrawer(true)} />}
      <CustomDrawer open={isDrawerOpen} onClose={toggleDrawer(false)}>
        <SettingsForm
          onSettingsSave={handleSettingsSave}
          onClose={toggleDrawer(false)}
          onCreatePONumbers={onCreatePONumbers}
          onSaveDates={onSaveDates}
          onDeleteDates={onDeleteDates}
          hasXPog={isXPogSelected}
          selectedCount={selections?.length || 0}
        />
      </CustomDrawer>
    </Container>
  );
}

export default memo(StoreSuppliesPogs);
